import { Button } from '@/components/ui/button';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Bug,
  ChevronDown,
  ChevronUp,
  Home,
  RefreshCw,
  Terminal,
  XCircle,
} from 'lucide-react';
import type { ErrorInfo, ReactNode } from 'react';
import { Component, useState } from 'react';
import { Link } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="min-h-screen bg-gradient-to-br from-background/95 to-muted/30 flex items-center justify-center p-4">
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-2xl w-full"
              >
                <div className="bg-background/90 backdrop-blur-lg border border-border/50 rounded-2xl shadow-2xl overflow-hidden">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-destructive/10 to-transparent opacity-30" />
                    <div className="relative p-6 flex items-center gap-4">
                      <div className="bg-destructive/20 p-3 rounded-full backdrop-blur-sm">
                        <AlertTriangle
                          className="h-8 w-8 text-destructive"
                          strokeWidth={1.5}
                        />
                      </div>
                      <div>
                        <h1 className="text-2xl font-bold text-foreground tracking-tight">
                          Erro Inesperado
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                          Nosso sistema encontrou um problema
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="bg-muted/30 border border-border/30 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="font-medium text-foreground">
                            O que aconteceu?
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {this.state.error?.message}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Button
                        variant="outline"
                        onClick={this.handleReset}
                        className="gap-2 h-12 hover:bg-secondary/80 transition-colors"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Tentar novamente
                      </Button>

                      <Button
                        onClick={() =>
                          this.setState({
                            hasError: false,
                            error: undefined,
                            errorInfo: undefined,
                          })
                        }
                        className="gap-2 h-12"
                      >
                        <Link to="/admin/dashboard">
                          <Home className="h-4 w-4" />
                          Voltar para home
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <ErrorDetails
                    error={this.state.error}
                    errorInfo={this.state.errorInfo}
                  />
                </div>

                <div className="mt-6 text-center">
                  <p className="text-xs text-muted-foreground">
                    Código do erro: {this.state.error?.name || 'UNKNOWN'}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

const ErrorDetails = ({ error, errorInfo }: { error?: Error; errorInfo?: ErrorInfo }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-t border-border/30 px-6 py-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4" />
          <span>Detalhes técnicos</span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 transition-transform" />
        ) : (
          <ChevronDown className="h-5 w-5 transition-transform" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-4 bg-muted/20 border border-border/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Bug className="h-4 w-4 text-foreground/60 mt-1 flex-shrink-0" />
                <div className="w-full">
                  <h4 className="text-xs font-mono font-semibold text-foreground/80 mb-2">
                    Stack Trace
                  </h4>
                  <pre className="text-xs font-mono text-muted-foreground overflow-auto max-h-60 p-3 bg-muted/50 rounded">
                    {error?.stack}
                  </pre>
                </div>
              </div>

              {errorInfo && (
                <div className="mt-4 flex items-start gap-3">
                  <Terminal className="h-4 w-4 text-foreground/60 mt-1 flex-shrink-0" />
                  <div className="w-full">
                    <h4 className="text-xs font-mono font-semibold text-foreground/80 mb-2">
                      Component Stack
                    </h4>
                    <pre className="text-xs font-mono text-muted-foreground overflow-auto max-h-60 p-3 bg-muted/50 rounded">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
